// ConfirmationModal.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Fade,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const iconBySeverity = {
  error: ErrorOutlineIcon,
  warning: WarningAmberRoundedIcon,
  info: InfoOutlinedIcon,
  success: CheckCircleOutlineIcon,
};

const ConfirmationModal = ({
  open,
  handleConfirm,
  handleCancel,
  title = '¿Cerrar sesión?',
  description = '¿Estás seguro de que deseas salir del sistema de pagos?',
  confirmText = 'Sí, cerrar sesión',
  cancelText = 'Cancelar',
  severity = 'warning',
}) => {
  const Icon = iconBySeverity[severity] || LogoutIcon;

  return (
    <Dialog
      open={open}
      disableRestoreFocus
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleCancel?.();
      }}
      maxWidth="xs"
      fullWidth
      keepMounted
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        sx: {
          borderRadius: '18px',
          padding: 'clamp(14px, 2.5vh, 22px) clamp(14px, 2.5vw, 20px)',
          maxWidth: 'clamp(270px, 26vw, 350px)',
          width: '85%',
          boxShadow: '0 16px 36px rgba(0, 0, 0, 0.22)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          backgroundColor: '#fafbfc',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0, pt: 0.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              bgcolor: '#fef2f2',
              color: '#ef4444',
              width: 'clamp(36px, 4vh, 44px)',
              height: 'clamp(36px, 4vh, 44px)',
              mb: 0.75,
              border: '2px solid #fee2e2',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
            }}
          >
            <LogoutIcon sx={{ fontSize: 'clamp(18px, 2.2vh, 22px)' }} />
          </Avatar>

          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em', fontSize: 'clamp(0.95rem, 1.8vh, 1.15rem)' }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.25, pb: 1.25, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, px: 0.5, fontSize: 'clamp(0.74rem, 1.2vh, 0.82rem)' }}>
          {description}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 0.5, pb: 0.25, justifyContent: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            flex: 1,
            borderRadius: '10px',
            borderColor: '#cbd5e1',
            color: '#475569',
            fontWeight: 700,
            textTransform: 'none',
            py: 'clamp(7px, 1vh, 9px)',
            fontSize: 'clamp(0.76rem, 1.3vh, 0.84rem)',
            '&:hover': {
              borderColor: '#94a3b8',
              backgroundColor: '#f8fafc',
            },
          }}
        >
          {cancelText}
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          sx={{
            flex: 1.1,
            borderRadius: '10px',
            backgroundColor: '#ef4444',
            backgroundImage: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)',
            color: '#ffffff',
            fontWeight: 800,
            textTransform: 'none',
            py: 'clamp(7px, 1vh, 9px)',
            fontSize: 'clamp(0.76rem, 1.3vh, 0.84rem)',
            boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
            '&:hover': {
              backgroundColor: '#dc2626',
              boxShadow: '0 6px 14px rgba(239, 68, 68, 0.35)',
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;